import { backendServices } from './backend.service';

const BASE = '/admin/wallets';

export interface MarketBalanceData {
  primaryMarketTotal: number;
  secondaryMarketTotal: number;
  rentalMarketTotal: number;
  adminBalance: number;
  adminWalletAddress?: string;
}

interface MarketBalanceResponse {
  status: boolean;
  message?: string;
  data?: MarketBalanceData;
}

export async function getMarketBalance(): Promise<MarketBalanceData> {
  const res = await backendServices.get<MarketBalanceResponse>(`${BASE}/balance`);
  if (!res.data?.status || !res.data?.data) {
    throw new Error(res.data?.message ?? 'Failed to load market balance');
  }
  return res.data.data;
}

export interface TransferRequest {
  toAddress: string;
  amount: number;
}

export interface TransferResponse {
  status: boolean;
  message?: string;
  data?: { txHash?: string };
  error?: string;
}

export async function transferFromAdmin(payload: TransferRequest): Promise<{ txHash?: string }> {
  const res = await backendServices.post<TransferResponse>(`${BASE}/transfer`, payload);
  if (!res.data?.status) {
    throw new Error(res.data?.message ?? res.data?.error ?? 'Transfer failed');
  }
  return res.data?.data ?? {};
}

export interface ReceiveTxRow {
  txHash: string;
  fromAddress: string;
  amount: number;
  timestamp: number;
}

interface ReceivesResponse {
  status: boolean;
  message?: string;
  data?: { receives: ReceiveTxRow[] };
  error?: string;
}

/** GET /admin/wallets/receives - incoming transfers to admin wallet (from backend RPC). */
export async function getIncomingTransfersFromApi(): Promise<ReceiveTxRow[]> {
  try {
    // Avoid caching: force fresh response so we don't get 304 with stale empty receives
    const res = await backendServices.get<ReceivesResponse>(`${BASE}/receives?t=${Date.now()}`);
    if (!res.data?.status) {
      console.warn(
        '[walletsApi] receives API returned status false:',
        res.data?.message ?? res.data
      );
      return [];
    }
    const receives = res.data?.data?.receives;
    return Array.isArray(receives) ? receives : [];
  } catch (err) {
    console.error('[walletsApi] getIncomingTransfersFromApi failed:', err);
    return [];
  }
}

/** Storage key and limit used by Wallets and AuditLogs so receives stay in sync. */
export const ADMIN_RECEIVES_STORAGE_KEY = 'vestin-admin-wallet-receives';
export const MAX_STORED_RECEIVES = 100;

/** Fetches receives from API, merges with existing localStorage, saves, and dispatches event so AuditLogs can refresh. */
export async function syncReceivesToStorage(): Promise<void> {
  try {
    const rows = await getIncomingTransfersFromApi();
    const existing: ReceiveTxRow[] = (() => {
      try {
        const s = localStorage.getItem(ADMIN_RECEIVES_STORAGE_KEY);
        if (!s) return [];
        const parsed = JSON.parse(s) as ReceiveTxRow[];
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    })();
    const byHash = new Map<string, ReceiveTxRow>();
    existing.forEach((r) => byHash.set(r.txHash, r));
    rows.forEach((r) => byHash.set(r.txHash, { ...r }));
    const merged = Array.from(byHash.values())
      .sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0))
      .slice(0, MAX_STORED_RECEIVES);
    localStorage.setItem(ADMIN_RECEIVES_STORAGE_KEY, JSON.stringify(merged));
    window.dispatchEvent(new CustomEvent('vestin-wallet-receives-updated'));
  } catch (err) {
    console.error('[syncReceivesToStorage]', err);
  }
}
